import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useUserTeamAccess } from '../config/queries'
import {
  useReactivateGuild,
  useRemoveCharactersFromGuild,
} from '../config/mutations'
import { useCustomDomain } from './useCustomDomain'

type ActionableEntity = {
  ids: string[]
  teamReference?: wowaudit.TeamReference
  text: string
  ctaPath?: string
  onCtaClick?: () => void
  ctaText: string
  ctaLoading?: boolean
  ctaDisabled?: boolean
  secondaryCtaText?: string
  onSecondaryCtaClick?: () => void
  secondaryCtaLoading?: boolean
  secondaryConfirm?: { description: string; continueText: string }
  dismissible?: boolean
  className?: string
}

export const useActionable = (user?: wowaudit.User) => {
  const { t } = useTranslation()
  const reactivateGuild = useReactivateGuild()
  const removeCharactersFromGuild = useRemoveCharactersFromGuild()
  const { customDomain } = useCustomDomain()

  const storedDismissedActions = JSON.parse(
    localStorage.getItem('dismissedActions') ?? '[]',
  )
  const [dismissedActions, setDismissedActions] = useState<string[]>(
    storedDismissedActions,
  )

  const dismissActionableEntity = (entity: ActionableEntity) => {
    const newDismissedActions = [...dismissedActions, ...entity.ids]
    setDismissedActions(newDismissedActions)
    localStorage.setItem(
      'dismissedActions',
      JSON.stringify(newDismissedActions),
    )
  }

  const actionableCharacters =
    user?.characterReferences?.filter(
      (ch) =>
        ch.actionable &&
        !dismissedActions.includes(`character_${ch.id}_${ch.gdprStatus}`),
    ) ?? []

  const candidateTeams =
    user?.teamReferences?.filter(
      (t) =>
        t.markedForDeletionAt &&
        !dismissedActions.includes(`team_${t.id}_${t.markedForDeletionAt}`),
    ) ?? []

  const inactiveGuildTeams = (user?.teamReferences ?? []).filter((team) => {
    const guild = team.ownerReference

    const isOnThisGuild = customDomain
      ? customDomain.guild.id === guild.id
      : window.location.pathname.startsWith(guild.path)

    return guild.active === false && isOnThisGuild
  })

  const missingGuildTeams = (user?.teamReferences ?? []).filter(
    (team) => team.ownerReference.exists === false,
  )

  const { data: accessForTeams } = useUserTeamAccess(
    Array.from(
      new Set([
        ...candidateTeams.map((t) => t.id),
        ...inactiveGuildTeams.map((t) => t.id),
        ...missingGuildTeams.map((t) => t.id),
      ]),
    ),
  )

  const actionableTeams = candidateTeams.filter(
    (t) => accessForTeams?.[t.id.toString()],
  )

  const collectAccessibleGuilds = (teams: wowaudit.TeamReference[]) =>
    Object.values(
      teams.reduce((guilds, team) => {
        if (accessForTeams?.[team.id.toString()]) {
          guilds[team.ownerReference.id] = team.ownerReference
        }

        return guilds
      }, {} as Record<string, wowaudit.GuildReference>),
    )

  const actionableGuilds = collectAccessibleGuilds(inactiveGuildTeams)

  const missingGuilds = collectAccessibleGuilds(missingGuildTeams)

  const actionableEntities: ActionableEntity[] = [
    ...actionableGuilds.map((entity) => ({
      ids: [`guild_${entity.id}_inactive`],
      text: t('profile.guild-inactive-warning', {
        name: entity.name,
      }),
      onCtaClick: () => reactivateGuild.mutate(entity),
      ctaText: t('profile.reactivate-guild'),
      ctaLoading:
        reactivateGuild.isPending &&
        reactivateGuild.variables?.id === entity.id,
      ctaDisabled: reactivateGuild.isPending,
      secondaryCtaText: t('profile.dismiss-guild'),
      onSecondaryCtaClick: () => removeCharactersFromGuild.mutate(entity),
      secondaryCtaLoading:
        removeCharactersFromGuild.isPending &&
        removeCharactersFromGuild.variables?.id === entity.id,
      secondaryConfirm: {
        description: t('profile.dismiss-guild-confirm', { name: entity.name }),
        continueText: t('profile.dismiss-guild'),
      },
      dismissible: true,
      className: 'bg-warning-foreground',
    })),
    ...missingGuilds.map((entity) => ({
      ids: [`guild_${entity.id}_missing`],
      text: t('profile.guild-missing-warning', {
        name: entity.name,
      }),
      ctaPath: window.location.pathname.startsWith(entity.path)
        ? undefined
        : entity.path,
      ctaText: t('profile.view-guild'),
      secondaryCtaText: t('profile.dismiss-guild'),
      onSecondaryCtaClick: () => removeCharactersFromGuild.mutate(entity),
      secondaryCtaLoading:
        removeCharactersFromGuild.isPending &&
        removeCharactersFromGuild.variables?.id === entity.id,
      secondaryConfirm: {
        description: t('profile.dismiss-guild-confirm', { name: entity.name }),
        continueText: t('profile.dismiss-guild'),
      },
      dismissible: true,
      className: 'bg-warning-foreground',
    })),
    ...actionableTeams.map((entity) => ({
      ids: [`team_${entity.id}_${entity.markedForDeletionAt}`],
      teamReference: entity,
      text: t('profile.team-requires-action-warning', {
        name: entity.name,
        owner: entity.ownerReference.name,
      }),
      ctaPath:
        window.location.pathname === `${entity.path}/settings#team`
          ? undefined
          : `${entity.path}/settings#team`,
      ctaText: t('profile.team-settings'),
      className: 'bg-destructive-foreground',
    })),
    ...(actionableCharacters.length
      ? [
          {
            ids: actionableCharacters.map(
              (ch) => `character_${ch.id}_${ch.gdprStatus}`,
            ),
            text: t('profile.character-requires-action-warning'),
            ctaPath:
              window.location.pathname === '/profile/characters'
                ? undefined
                : '/profile/characters',
            ctaText: t('header.profile'),
            className: 'bg-warning-foreground',
          },
        ]
      : []),
  ]

  const [currentActionIndex, setCurrentActionIndex] = useState<number>(0)
  const currentAction: ActionableEntity | undefined =
    actionableEntities[currentActionIndex]

  const hasNextAction = currentActionIndex < actionableEntities.length - 1
  const hasPreviousAction = currentActionIndex > 0

  return {
    actionableEntities,
    currentAction,
    currentActionIndex,
    setCurrentActionIndex,
    hasNextAction,
    hasPreviousAction,
    dismissActionableEntity,
  }
}
